import { useEffect, useState } from "react";
import { trackEvent } from "@/lib/analytics";
import OnboardingScreen1 from "./OnboardingScreen1";
import OnboardingScreen2 from "./OnboardingScreen2";
import OnboardingScreen3 from "./OnboardingScreen3";

interface OnboardingOverlayProps {
  onDone: () => void;
}

const SCREENS = [OnboardingScreen1, OnboardingScreen2, OnboardingScreen3];

/**
 * Wave 4 Commit 1 — 3-screen value walkthrough overlay.
 *
 * Analytics events (prefixed onboarding_v2_):
 *  - onboarding_v2_shown       fired once on mount
 *  - onboarding_v2_next_tapped fired on each Next tap (with `to_index`)
 *  - onboarding_v2_skipped     fired on Skip
 *  - onboarding_v2_completed   fired when finishing the last screen
 */
export default function OnboardingOverlay({ onDone }: OnboardingOverlayProps) {
  const [idx, setIdx] = useState(0);
  const total = SCREENS.length;
  const Screen = SCREENS[idx];
  const isLast = idx === total - 1;

  useEffect(() => {
    trackEvent("onboarding_v2_shown", {});
  }, []);

  const handleNext = () => {
    if (isLast) {
      trackEvent("onboarding_v2_completed", { screens: total });
      onDone();
      return;
    }
    const to = idx + 1;
    trackEvent("onboarding_v2_next_tapped", { from_index: idx, to_index: to });
    setIdx(to);
  };

  const handleSkip = () => {
    trackEvent("onboarding_v2_skipped", { at_index: idx });
    onDone();
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Welcome to Like2Fit"
      className="l2f-ob-root"
    >
      <div className="l2f-ob-topbar">
        <div className="l2f-ob-progress" aria-hidden>
          {Array.from({ length: total }).map((_, i) => (
            <span
              key={i}
              className={
                "l2f-ob-dot" + (i === idx ? " l2f-ob-dot-on" : "")
              }
            />
          ))}
        </div>
        <button
          type="button"
          onClick={handleSkip}
          className="l2f-ob-skip"
          aria-label="Skip walkthrough"
        >
          Skip
        </button>
      </div>

      <div className="l2f-ob-stage">
        <Screen />
      </div>

      <div className="l2f-ob-footer">
        <button
          type="button"
          onClick={handleNext}
          className="l2f-ob-next"
        >
          {isLast ? "Try your first outfit" : "Next"}
        </button>
      </div>

      <style>{`
        .l2f-ob-root {
          position: fixed;
          inset: 0;
          z-index: 300;
          display: flex;
          flex-direction: column;
          background: #FAECE7;
          color: #2C2820;
          padding-top: calc(var(--safe-top, 0px) + 12px);
          padding-bottom: calc(var(--safe-bottom, 0px) + 16px);
          overflow: hidden;
        }
        .l2f-ob-topbar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 4px 20px 8px;
        }
        .l2f-ob-progress {
          display: flex;
          gap: 6px;
        }
        .l2f-ob-dot {
          width: 24px;
          height: 3px;
          border-radius: 2px;
          background: rgba(44,40,32,0.15);
          transition: background 200ms ease;
        }
        .l2f-ob-dot-on { background: #C84B2F; }
        .l2f-ob-skip {
          background: transparent;
          border: none;
          font: inherit;
          color: #6B6560;
          font-size: 14px;
          padding: 6px 4px;
          cursor: pointer;
        }
        .l2f-ob-stage {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0 24px;
          overflow-y: auto;
          animation: l2f-ob-fade 260ms ease;
        }
        .l2f-ob-footer {
          padding: 12px 20px 0;
        }
        .l2f-ob-next {
          width: 100%;
          padding: 16px 20px;
          border-radius: 999px;
          border: none;
          background: #2C2820;
          color: #FAECE7;
          font-size: 16px;
          font-weight: 600;
          letter-spacing: 0.01em;
          cursor: pointer;
          transition: transform 120ms ease;
        }
        .l2f-ob-next:active { transform: scale(0.98); }

        .l2f-ob-screen {
          max-width: 420px;
          width: 100%;
          display: flex;
          flex-direction: column;
          gap: 20px;
        }
        .l2f-ob-eyebrow {
          font-size: 11px;
          letter-spacing: 0.14em;
          color: #C84B2F;
          font-weight: 600;
        }
        .l2f-ob-headline {
          font-family: var(--font-display, Georgia, serif);
          font-style: italic;
          font-weight: 400;
          font-size: 34px;
          line-height: 1.08;
          letter-spacing: -0.01em;
          color: #2C2820;
          margin: 0;
        }
        .l2f-ob-body {
          font-size: 16px;
          line-height: 1.5;
          color: #6B6560;
          margin: 0;
        }
        .l2f-ob-visual {
          margin-top: 12px;
          padding: 24px;
          border-radius: 20px;
          background: #FFFCF8;
          border: 0.5px solid rgba(44,40,32,0.08);
        }
        .l2f-ob-visual-row {
          display: flex;
          align-items: center;
          gap: 16px;
          justify-content: center;
        }
        .l2f-ob-chip {
          width: 64px;
          height: 64px;
          border-radius: 16px;
          background: #FAECE7;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 30px;
        }
        .l2f-ob-chip-dim {
          background: transparent;
          border: 1.5px dashed rgba(44,40,32,0.25);
          color: #9B958E;
          font-family: var(--font-display, Georgia, serif);
          font-style: italic;
          font-size: 26px;
        }
        .l2f-ob-arrow {
          color: #C84B2F;
          font-size: 22px;
          font-family: var(--font-display, Georgia, serif);
          font-style: italic;
        }
        .l2f-ob-card-stack {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
        .l2f-ob-mini-card {
          padding: 14px 18px;
          border-radius: 14px;
          background: #FAECE7;
          font-size: 14px;
          color: #2C2820;
          font-weight: 500;
        }
        .l2f-ob-verdict-row {
          display: flex;
          gap: 10px;
          justify-content: center;
          flex-wrap: wrap;
        }
        .l2f-ob-pill {
          padding: 10px 18px;
          border-radius: 999px;
          font-size: 14px;
          font-weight: 600;
          letter-spacing: 0.02em;
        }
        .l2f-ob-pill-buy  { background: #E3F0E4; color: #1F5A2A; }
        .l2f-ob-pill-call { background: #F6EBD6; color: #7A5A17; }
        .l2f-ob-pill-skip { background: #F3DAD3; color: #7A2A16; }

        @keyframes l2f-ob-fade {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        @media (prefers-reduced-motion: reduce) {
          .l2f-ob-stage { animation: none; }
          .l2f-ob-next  { transition: none; }
          .l2f-ob-dot   { transition: none; }
        }

        @media (prefers-color-scheme: dark) {
          .l2f-ob-root { background: #1A1613; color: #F5EDE6; }
          .l2f-ob-dot { background: rgba(245,237,230,0.18); }
          .l2f-ob-dot-on { background: #E86A4E; }
          .l2f-ob-skip { color: #A8A099; }
          .l2f-ob-next { background: #F5EDE6; color: #1A1613; }
          .l2f-ob-headline { color: #F5EDE6; }
          .l2f-ob-body { color: #A8A099; }
          .l2f-ob-eyebrow { color: #E86A4E; }
          .l2f-ob-visual { background: #241E1A; border-color: rgba(245,237,230,0.08); }
          .l2f-ob-chip { background: #2E2721; }
          .l2f-ob-chip-dim { background: transparent; border-color: rgba(245,237,230,0.22); color: #A8A099; }
          .l2f-ob-mini-card { background: #2E2721; color: #F5EDE6; }
          .l2f-ob-arrow { color: #E86A4E; }
          .l2f-ob-pill-buy  { background: #1F3A24; color: #B9E0BE; }
          .l2f-ob-pill-call { background: #3A2F18; color: #E9CE8E; }
          .l2f-ob-pill-skip { background: #3A1F17; color: #E9B0A2; }
        }
      `}</style>
    </div>
  );
}
